import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import CustomCard from '../../../components/card/CustomCard'
import CustomCardHeader from '../../../components/card/CustomCardHeader'
import CardBody from '../../../components/card/CardBody'
import CustomTable, { TableRow, TableHeader, TableCell } from '../../../components/table/CustomTable'
import Pagination from '../../../components/Pagination'
import ComponentLoader, { ComponentError } from '../../../components/ComponentLoader'
import EmptyState from '../../../components/EmptyState'
import {
  tryFetchUserBalance,
  tryFetchUserPoints,
} from '../../../redux/slices/rewardPointsSlice'
import { isoToLocaleFormat } from '../../../helpers/dateTimeHelper'
import { fixTo2Place } from '../../../helpers/helperFunctions'

const StatusBadge = ({ status }) => {
  const colors = {
    DRAFT: 'bg-yellow-100 text-yellow-800',
    POSTED: 'bg-green-100 text-green-800',
    CANCEL: 'bg-red-100 text-red-800',
  }
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  )
}

export default function CustomerBalance() {
  const dispatch = useDispatch()
  const { id } = useParams()
  const [page, setPage] = useState(1)
  const perPage = 10
  const { balance, userPoints, total, loading, error } = useSelector((state) => state.rewardPoints)

  useEffect(() => {
    if (id) {
      dispatch(tryFetchUserBalance(id))
      dispatch(tryFetchUserPoints({ userId: id, params: { page, per_page: perPage } }))
    }
  }, [dispatch, id, page])

  const handlePageChange = ({ selected }) => {
    setPage(selected + 1)
  }

  const handleRetry = () => {
    if (id) {
      dispatch(tryFetchUserBalance(id))
      dispatch(tryFetchUserPoints({ userId: id, params: { page, per_page: perPage } }))
    }
  }

  const totalPages = Math.ceil(total / perPage)
  const showPagination = userPoints.length > 0 && total > perPage

  return (
    <div className="px-3 md:px-8 h-auto -mt-24">
      <div className="container mx-auto max-w-full">
        <div className="grid grid-cols-1 px-4 mb-16">
          <CustomCard>
            <CustomCardHeader color="blue" contentPosition="none">
              <div className="flex items-center justify-between w-full">
                <h2 className="text-2xl text-white">Customer Balance</h2>
                <span className="text-sm text-white text-opacity-80">ID: {id}</span>
              </div>
            </CustomCardHeader>
            <CardBody>
              {loading && !balance ? (
                <ComponentLoader />
              ) : error ? (
                <ComponentError errorMsg={error} retry={handleRetry} />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-secondary-100 rounded-lg">
                      <p className="text-xs text-rand-200 mb-1 uppercase tracking-wide">Posted Points</p>
                      <p className="text-2xl font-bold text-rand-100">
                        {balance?.total_posted_points != null ? fixTo2Place(balance.total_posted_points) : '0'} points
                      </p>
                    </div>
                    <div className="p-4 bg-secondary-100 rounded-lg">
                      <p className="text-xs text-rand-200 mb-1 uppercase tracking-wide">Draft Points</p>
                      <p className="text-2xl font-bold text-rand-100">
                        {balance?.total_draft_points != null ? fixTo2Place(balance.total_draft_points) : '0'} points
                      </p>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-rand-100 mb-4">Point History</h3>

                  {userPoints.length === 0 ? (
                    <EmptyState icon="receipt_long" message="No points found" />
                  ) : (
                    <>
                      <CustomTable>
                        <thead>
                          <TableRow>
                            <TableHeader>Status</TableHeader>
                            <TableHeader>Points</TableHeader>
                            <TableHeader>Given For</TableHeader>
                            <TableHeader>Reference</TableHeader>
                            <TableHeader>Source</TableHeader>
                            <TableHeader>Date</TableHeader>
                          </TableRow>
                        </thead>
                        <tbody>
                          {userPoints.map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell>
                                <StatusBadge status={tx.status} />
                              </TableCell>
                              <TableCell>
                                <span className={`text-sm font-semibold ${tx.point > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {tx.point > 0 ? '+' : ''}{fixTo2Place(tx.point)}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-rand-100">{tx.given_for || '-'}</TableCell>
                              <TableCell className="text-sm text-rand-100">{tx.reference_id || '-'}</TableCell>
                              <TableCell>
                                <span className="text-sm text-rand-100">{tx.source_system || '-'}</span>
                              </TableCell>
                              <TableCell className="text-sm text-rand-200">
                                {tx.created_at ? isoToLocaleFormat(tx.created_at) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </tbody>
                      </CustomTable>

                      {showPagination && (
                        <div className="mt-4">
                          <Pagination
                            pageCount={totalPages}
                            onPageChange={handlePageChange}
                            forcePage={page - 1}
                          />
                          <p className="text-center text-xs text-rand-200 mt-2">
                            Showing {((page - 1) * perPage) + 1}-
                            {Math.min(page * perPage, total)} of {total} results
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </CardBody>
          </CustomCard>
        </div>
      </div>
    </div>
  )
}